/**
 * The plain-English rules lines the server writes for a Pick'em pool
 * (`describePickemRules` in the contest engine, served by setup-preview and
 * pool-get). Rendered as is: the client never composes a rule sentence.
 */
import type { ReactNode } from "react";

import {
  PICKEM_RULES_PREVIEW_HEADING,
  PICKEM_RULES_PREVIEW_PENDING,
} from "./pickemCopy";

export const PickemRulesLines = ({
  lines,
  testId = "pickem-rules-lines",
}: {
  lines: readonly string[];
  testId?: string | undefined;
}): ReactNode => (
  <ul className="flex list-disc flex-col gap-1 pl-6" data-testid={testId}>
    {lines.map((line) => (
      <li key={line} className="text-body text-ink">
        {line}
      </li>
    ))}
  </ul>
);

/**
 * "What your players will read" under the setup fields (create step, Settings
 * dialog, New contest dialog). While the draft is missing a choice there is no
 * server text for it yet, so the old text is replaced by a short hint instead
 * of describing rules the Commissioner already changed (ISSUE-1303).
 */
export const PickemRulesPreview = ({
  isDraftComplete,
  rulesLines,
  testId,
}: {
  isDraftComplete: boolean;
  rulesLines: readonly string[] | null;
  testId?: string | undefined;
}): ReactNode => {
  if (!isDraftComplete) {
    return (
      <section className="flex flex-col gap-2">
        <h2 className="text-body-strong text-ink">
          {PICKEM_RULES_PREVIEW_HEADING}
        </h2>
        <p
          className="text-body text-ink-secondary"
          data-testid="pickem-rules-preview-pending"
        >
          {PICKEM_RULES_PREVIEW_PENDING}
        </p>
      </section>
    );
  }
  if (rulesLines === null || rulesLines.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" data-testid="pickem-setup-rules">
      <h2 className="text-body-strong text-ink">
        {PICKEM_RULES_PREVIEW_HEADING}
      </h2>
      <PickemRulesLines lines={rulesLines} testId={testId} />
    </section>
  );
};
